import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { INDIA_PATH } from "./data/indiaPath";

// India approximate bounding box (matches indiaPath projection)
const LNG_MIN = 67.5, LNG_MAX = 97.5;
const LAT_MIN = 6.5, LAT_MAX = 37.5;
const W = 600, H = 700;

const project = (lat, lng) => ({
  x: ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * W,
  y: ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H,
});

const COLOR_BY_TYPE = {
  "Business": "#8FCAE7",
  "Leisure - North": "#FFCC00",
  "Leisure - South": "#FF69B4",
};

export default function IndiaMap({ properties = [] }) {
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="relative w-full h-full" data-testid="india-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* dotted grid */}
        <defs>
          <pattern id="dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.6" fill="#D4D4D8" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="url(#dots)" />

        {/* India outline */}
        <path d={INDIA_PATH} fill="#F4F4F5" stroke="#09090B" strokeWidth="1.5" />

        {/* property markers */}
        {properties.map((p) => {
          const { x, y } = project(p.lat, p.lng);
          const r = Math.max(4, Math.min(12, p.capacity_beds / 25));
          const fill = COLOR_BY_TYPE[p.type] || "#09090B";
          const isHover = hover === p.location;
          return (
            <g key={p.location} onMouseEnter={() => setHover(p.location)} onMouseLeave={() => setHover(null)} onClick={() => navigate(`/operational/${encodeURIComponent(p.location)}`)} className="cursor-pointer">
              <circle cx={x} cy={y} r={r + 6} fill={fill} opacity={isHover ? 0.25 : 0.15} />
              <circle
                cx={x}
                cy={y}
                r={r}
                fill={fill}
                stroke="#09090B"
                strokeWidth={isHover ? 2 : 1}
                data-testid={`map-marker-${p.location}`}
              />
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hover && (() => {
        const p = properties.find(x => x.location === hover);
        if (!p) return null;
        const { x, y } = project(p.lat, p.lng);
        const left = (x / W) * 100;
        const top = (y / H) * 100;
        return (
          <div
            className="absolute pointer-events-none bg-brand-ink text-white px-3 py-2 border-2 border-brand-yellow font-mono-tech text-[11px] z-10"
            style={{ left: `${left}%`, top: `${top}%`, transform: "translate(12px, -100%)" }}
            data-testid="map-tooltip"
          >
            <div className="font-display font-bold text-sm text-brand-yellow">{p.location}</div>
            <div className="opacity-80 mt-0.5">{p.city}, {p.state}</div>
            <div className="mt-1.5 space-y-0.5">
              <div>{p.capacity_beds} beds · {p.occupancy_pct}% occ</div>
              <div>{p.type} · {p.model}</div>
            </div>
          </div>
        );
      })()}

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex flex-col gap-1.5 bg-white/95 border border-brand-line p-2.5" data-testid="map-legend">
        <div className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">LEGEND</div>
        {Object.entries(COLOR_BY_TYPE).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-wide">
            <span className="w-3 h-3 border border-brand-ink" style={{ background: v }}></span>
            <span className="text-zinc-700">{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
